import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { PowerbivalidatorService } from '@services/filevalidators/powerbivalidator.service';
import { NotificationService } from '@services/notification.service';
// import { CDK_CONNECTED_OVERLAY_SCROLL_STRATEGY_PROVIDER } from '@angular/cdk/overlay/overlay-directives';
import { SessionStorageService } from '@services/storage.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { SimpleeditdialogComponent } from '@shared/components/simpleeditdialog/simpleeditdialog.component';
import { aciesDateFormat } from '@shared/helpers';
import { ExternalUrlService } from '@shared/services/externalurl.service';
import { combineLatest, map, Subject, take, takeUntil, tap } from 'rxjs';
import { IDeleteReport, IReport, ReportService } from '../../../shared/services/report.service';
import { UserService } from '../../../shared/services/user.service';
import { DialogExturlComponent } from './exturl/dialog-exturl/dialog-exturl.component';

interface ITableReport extends IReport {
	status: string;
	url?: string;
	categoryName?: string;
	categoryId?: string;
}

interface ITableFilter {
	status: string;
	name: string;
}

@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AdminReportsComponent implements OnInit, OnDestroy {
	displayedColumns: string[] = [
		'select',
		'name',
		'type',
		'categoryName',
		// 'reportId',
		'externalId',
		'groupId',
		'url',
		'createdDate',
		'updatedDate',
		'status',
		'menu',
	];

	dataSource = new MatTableDataSource();
	showSearch = false;
	isLoading = false;
	isAllSelected = false;
	resultsLength = 0;
	selected: IReport[] = [];
	activeOnly = true;
	originalDatasource = new MatTableDataSource();
	unsubscribe: Subject<void> = new Subject<void>();
	activeLabel = 'active';
	inactiveLabel = 'inactive';
	powerbiLabel = 'powerbi';
	externalLabel = 'external';
	searchText = '';
	permissions = [];
	loadingSubject: Subject<boolean> = new Subject<boolean>();
	aciesDateFormat: (date: Date, withTime?: boolean, withSeconds?: boolean) => string;

	availableCategories: ReportCategory[];

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_delete',
			text: () => 'insights.administration.reports.delete.deletebtn',
			click: () => this.deleteReport(),
			disabled: () => this.selected.length === 0,
		},
		{
			icon: () => 'acies_add_alt',
			text: () => 'insights.administration.reports.categories.categories',
			click: () => this.openReportCategories(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add_alt',
			text: () => 'insights.administration.reports.externalurl.externalurl',
			click: () => this.openExternalUrls(),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'insights.administration.reports.upload.button',
			click: () => this.openUploadFile(),
		},
	];

	constructor(
		private reportService: ReportService,
		public dialog: MatDialog,
		private translate: TranslateService,
		private userService: UserService,
		private sessionStorageService: SessionStorageService,
		private injector: Injector,
		private notificationService: NotificationService,
		public changeDetection: ChangeDetectorRef,
		private externalUrlService: ExternalUrlService,
	) {
		this.dataSource.filterPredicate = ((data, filter) => {
			const filterObj: ITableFilter = filter;
			const filteredKeys = ['name', 'categoryName', 'externalId', 'groupId', 'createdDate', 'updatedDate'];
			const a = !filterObj.status || data.status === filterObj.status;
			let b = false;

			for (const key in data) {
				if (!filteredKeys.includes(key)) continue;
				if (typeof data[key] === 'string') {
					b = data[key].trim().toLowerCase().includes(filterObj.name);
				}
				if (data[key] instanceof Date) {
					b = this.aciesDateFormat(data[key], true).trim().toLowerCase().includes(filterObj.name);
				}
				if (b) break;
			}

			return a && b;
		}) as (ITableReport, ITableFilter) => boolean;
	}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.aciesDateFormat = aciesDateFormat;
		this.isLoading = true;
		this.loadReports();

		this.permissions = JSON.parse(this.sessionStorageService.get('User.Permissions'));

		setTimeout(() => {
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;

			// Don't sort lowercase after uppercase
			this.dataSource.sortingDataAccessor = (item, property) => {
				console.log(item, property);
				if (item[property] instanceof Date) {
					return item[property].getTime();
				}
				if (typeof item[property] === 'boolean' || item[property] === 'number' || !item[property]) {
					return item[property];
				}
				return item[property].toLocaleLowerCase();
			};

			this.sort.sortChange.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
				this.paginator.firstPage();
			});
		});
	}

	loadReports() {
		combineLatest([this.reportService.fetchReports().pipe(map((res) => res.result)), this.reportService.loadReportCategories(), this.externalUrlService.fetchExternalUrls().pipe(map((res) => res.result))]).subscribe((data) => {
			const tableReports: ITableReport[] = [];
			if (data[0] && data[1] && data[2]) {
				const categories = data[1];
				data[0]
					.map((element: ITableReport) => {
						const categoryName = element.categoryId && categories.find((c) => c.id === element.categoryId) ? categories.find((c) => c.id === element.categoryId).name : '';
						return {
							id: element.id,
							externalId: element.externalId,
							groupId: element.groupId,
							name: element.name,
							reportId: element.id,
							isDeleted: element.isDeleted,
							status: element.isDeleted ? this.inactiveLabel : this.activeLabel,
							type: this.powerbiLabel,
							categoryName,
							createdDate: new Date(element.createdAt * 1000),
							updatedDate: new Date(element.updatedAt * 1000),
						};
					})
					.forEach((r) => tableReports.push(r));
				data[2]
					.map((element: ITableReport) => {
						const categoryName = element.categoryId && categories.find((c) => c.id === element.categoryId) ? categories.find((c) => c.id === element.categoryId).name : '';
						return {
							id: element.id,
							url: element.url,
							name: element.name,
							externalId: '',
							groupId: '',
							isDeleted: element.isDeleted,
							status: element.isDeleted ? this.inactiveLabel : this.activeLabel,
							type: this.externalLabel,
							categoryName,
							createdDate: new Date(element.createdAt * 1000),
							updatedDate: new Date(element.updatedAt * 1000),
						};
					})
					.forEach((r) => tableReports.push(r));
				this.dataSource.data = tableReports;
				this.originalDatasource = this.dataSource;
				this.setInitialFilter();
				this.resultsLength = this.dataSource.filteredData.length;
			} else {
				this.dataSource.data = [];
				this.originalDatasource = this.dataSource;
				this.resultsLength = 0;
			}
			this.isLoading = false;
		});
	}

	applyFilter(event: Event) {
		console.log('apply filter');
		const filterValue = (event.target as HTMLInputElement).value;
		this.searchText = filterValue;
		const filter = {
			status: this.activeOnly ? this.activeLabel : '',
			name: filterValue.trim().toLowerCase(),
		} as unknown as string;
		this.dataSource.filter = filter;
		this.resultsLength = this.dataSource.filteredData.length;
	}

	applyActiveOnlyFilter() {
		this.activeOnly = !this.activeOnly;
		const filter = {
			name: this.searchText,
			status: this.activeOnly ? this.activeLabel : '',
		} as unknown as string;
		this.dataSource.filter = filter;
		this.resultsLength = this.dataSource.filteredData.length;
	}

	setInitialFilter() {
		const filter = {
			name: '',
			status: this.activeOnly ? this.activeLabel : '',
		} as unknown as string;
		this.dataSource.filter = filter;
		this.resultsLength = this.dataSource.filteredData.length;
	}

	select(report: IReport) {
		const index = this.selected.findIndex((x) => x.id === report.id);
		if (index > -1) {
			this.selected.splice(index, 1);
		} else {
			this.selected.push(report);
		}
		this.isAllSelected = this.dataSource.data.length === this.selected.length;
	}

	selectSingle(_: IReport) {
		this.selected = [];
		this.isAllSelected = false;
	}

	selectOrClearAll() {
		if (this.dataSource.data.filter((r) => r['isDeleted'] === false).length === this.selected.length) {
			this.selected = [];
			this.isAllSelected = false;
		} else {
			this.selected = [];
			this.dataSource.data.forEach((element: IReport) => {
				if (element.isDeleted === false) this.selected.push(element);
			});
			this.isAllSelected = true;
		}
	}

	deleteReport() {
		if (this.selected.length === 0) return;
		console.log('deleting reports');
		const dialogRef = this.dialog.open(DialogDataDeleteDialogComponent, {
			width: '400px',
			data: this.selected,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result != null && this.selected.length > 0) {
					const deleteReps: IDeleteReport[] = [];
					const deleteExtUrls: IDeleteReport[] = [];
					this.selected.forEach((item) => {
						if (item.url?.length > 0) {
							deleteExtUrls.push({
								id: item.id,
								isDeleted: true,
								name: item.name,
							});
						} else {
							deleteReps.push({
								id: item.id,
								isDeleted: true,
								name: item.name,
							});
						}
					});
					this.deleteOrReactivateReports(deleteReps, deleteExtUrls);
					this.selected = [];
					this.isAllSelected = false;
				}
			});
	}

	deleteOrReactivateReports(reports: IDeleteReport[], extUrls: IDeleteReport[], reactivating: boolean = false) {
		const reportNames = reports.map((r) => {
			const s = `"${r.name}"`;
			delete r.name; // Make sure 'name' does not exist in the API payload for deleteReports()
			return s;
		});
		const extUrlNames = extUrls.map((r) => {
			const s = `"${r.name}"`;
			delete r.name; // Make sure 'name' does not exist in the API payload for deleteReports()
			return s;
		});
		const allNames = [...reportNames, ...extUrlNames];
		const translateKey = reportNames.length > 1 ? 'multiple' : 'single';
		combineLatest([this.reportService.deleteReports(reports).pipe(map((res) => res.result)), this.externalUrlService.deleteExternalUrls(extUrls).pipe(map((res) => res.result))]).subscribe(() => {
			this.isLoading = true;
			this.activeOnly = !reactivating;
			this.updateReportsList();
			this.notificationService.notify([`insights.report.${reactivating ? 'reactivated' : 'deleted'}notification_${translateKey}_heading`], [`insights.report.${reactivating ? 'reactivated' : 'deleted'}notification_${translateKey}_text`, { reportName: allNames.join(', ') }], 'success');
		});
	}

	updateReportsList() {
		this.isLoading = true;
		this.loadReports();

		// If "logged in as", fetch that user's reports, and do not show the list as admin
		const reportUserId = this.userService.impersonateUser ? this.userService.impersonateUser.id : null;
		const isAdmin = this.userService.impersonateUser ? false : this.permissions.findIndex((x) => x === 'insights:ReportCreate') > 0;
		this.reportService.fetchUserReports(reportUserId, isAdmin);
	}

	onReactivateReport(report: IReport) {
		const deleteReps: IDeleteReport[] = [];
		const deleteExtUrls: IDeleteReport[] = [];
		if (report.url?.length > 0) {
			deleteExtUrls.push({ id: report.id, isDeleted: false });
		} else {
			deleteReps.push({ id: report.id, isDeleted: false });
		}
		this.deleteOrReactivateReports(deleteReps, deleteExtUrls, true);
	}

	onEditExternalUrl(report: IReport) {
		const dialogRef = this.dialog.open(DialogExturlComponent, {
			width: '700px',
			data: report,
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				this.updateReportsList();
			});
	}

	onPermanentlyDeleteReport(report: IReport) {
		const reports: IReport[] = [];
		reports.push(report);
		const dialogRef = this.dialog.open(DialogDataDeleteDialogComponent, {
			width: '400px',
			data: reports,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result[0].url?.length > 0) {
					this.externalUrlService
						.permanentlyDeleteExternalUrl(result[0])
						.pipe(takeUntil(this.unsubscribe))
						.subscribe(() => {
							this.updateReportsListAndNotify(report.name);
						});
				} else {
					this.reportService
						.permanentlyDeleteReport(result[0])
						.pipe(takeUntil(this.unsubscribe))
						.subscribe(() => {
							this.updateReportsListAndNotify(report.name);
						});
				}
			});
	}

	private updateReportsListAndNotify(reportName: string) {
		this.isLoading = true;
		this.activeOnly = false;
		this.updateReportsList();
		this.notificationService.notify(['insights.report.permanentlydeletednotification_heading'], ['insights.report.permanentlydeletednotification_text', { reportName }], 'success');
	}

	openUploadFile() {
		// To avoid sluggish <input> fields in MatDialog, see https://stackoverflow.com/a/56447766/2347048
		// Remember to do: this.changeDetection.reattach() in callback, see below
		this.changeDetection.detach();

		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '550px',
			data: {
				headingKey: 'insights.administration.reports.upload.heading',
				subHeadingKey: 'insights.administration.reports.upload.subheading',
				allowedFileTypes: ['.pbix'],
				validatorService: this.injector.get(PowerbivalidatorService),
				// Vi kan sende dette subject med ind i fil-dialogen, så vi kan styre visningen af den component-specifikke loading-spinner inde fra file-uploaderen i stedet for at styre
				// upload-dialogens egen spinner
				// loadingHandler: this.loadingSubject,
				optionalFields: [
					{
						name: 'powerbi_report_title',
						titleKey: 'insights.administration.reports.upload.titlefield',
						mandatory: true,
					},
					// { name:'powerbi_report_category', titleKey: 'insights.administration.reports.upload.categoryfield', mandatory: true, values:[1,2,3] },
				],
				processFileHook() {
					// Function injected to run when the file list is prepared
					this.formData['powerbi_report_title'].setValue(this.files[0].name.split('.').shift());
				},
			},
		});
		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				this.changeDetection.reattach(); // <--- See above for explanation

				if (result != null) {
					// this.isLoading = true;
					setTimeout(() => {
						this.updateReportsList();
					}, 15 * 1000);
				}
			});
	}

	public openReportCategories() {
		this.changeDetection.detach();
		this.dialog
			.open(DialogReportCategoriesComponent, {
				width: '800px',
				// minHeight: 'calc(100vh - 90px)',
				height: 'auto',
				maxHeight: '2000px',
				data: {
					selected: this.selected,
				},
				autoFocus: false, // To prevent the input field from being focused when the dialog opens
			})
			.afterClosed()
			.subscribe((selected) => {
				this.changeDetection.reattach();
				if (selected) this.selected = [];
				// this.reportService.fetchUserReports();
			});
	}

	public openExternalUrls() {
		console.log('open add extUrl dialog');
		const dialogRef = this.dialog.open(DialogExturlComponent, {
			width: '700px',
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(() => {
				this.updateReportsList();
			});
	}
}

@Component({
	selector: 'app-dialog-delete-report',
	templateUrl: 'dialog-delete-report.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogDataDeleteDialogComponent {
	constructor(
		public dialogRef: MatDialogRef<DialogDataDeleteDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: IReport[],
	) {}

	onNoClick(): void {
		this.dialogRef.close();
	}
}

export interface ReportCategory {
	id: string;
	name: string;
	sortOrder: number;
}

@Component({
	selector: 'app-dialog-report-categories',
	templateUrl: 'dialog-report-categories.html',
	styleUrls: ['dialog-report-categories.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogReportCategoriesComponent implements OnInit {
	public availableCategories: ReportCategory[] = [];
	sortedCategories: ReportCategory[] = [];
	public newCategoryForm: FormGroup = new FormGroup({
		categoryControl: new FormControl('', {
			validators: [Validators.required, Validators.pattern('^[ÆØÅæøåA-Za-z0-9._ ]{0,59}[ÆØÅæøåA-Za-z0-9._]$')],
			updateOn: 'change',
		}),
	});

	public assignCategoryForm: FormGroup = new FormGroup({
		assignCategoryControl: new FormControl(
			{ value: '', disabled: !this.data.selected.length },
			{
				validators: Validators.required,
				asyncValidators: [],
				updateOn: 'change',
			},
		),
	});

	public categoryErrors = [];

	constructor(
		public dialogRef: MatDialogRef<DialogReportCategoriesComponent>,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public reportService: ReportService,
		public externalUrlService: ExternalUrlService,
		private translator: TranslateService,
		private notificationService: NotificationService,
	) {}

	ngOnInit() {
		this.reloadCategories();
	}

	private reloadCategories() {
		this.reportService.loadReportCategories(true).subscribe((data) => {
			this.availableCategories = [...data].sort((a, b) => {
				const nA = a.name.toUpperCase();
				const nB = b.name.toUpperCase();
				if (nA === nB) return 0;
				return nA > nB ? 1 : -1;
			});

			this.sortedCategories = [...data].sort((a, b) => {
				const nA = a.sortOrder;
				const nB = b.sortOrder;
				if (nA === nB) return 0;
				return nA > nB ? 1 : -1;
			});
		});
	}

	addCategory(formData) {
		this.categoryErrors = [];
		if (this.availableCategories.filter((c) => this.cleanCategoryName(c.name) === this.cleanCategoryName(formData.categoryControl)).length) {
			this.categoryErrors.push(this.translator.instant('insights.administration.reports.categories.categoryexists', { categoryName: formData.categoryControl.trim() }));
			return;
		}

		const newCatId = formData.categoryControl.toLowerCase().trim().replace(/ /g, '_');

		this.reportService
			.addReportCategory(formData.categoryControl, newCatId, this.availableCategories.length + 1)
			.pipe(
				map((res) => res.result),
				tap((saveRes) => {
					this.notificationService.notify(['insights.administration.reports.categories.newcategory'], ['insights.administration.reports.categories.categorycreated', { categoryName: saveRes.name }], 'success');
				}),
			)
			.subscribe((saveRes) => {
				this.reloadCategories();
				setTimeout(() => {
					document.getElementById(saveRes.id).scrollIntoView({ behavior: 'smooth' });
					document.getElementById(saveRes.id).classList.add('new');
				}, 500);
			});
	}

	assignCategory(formData) {
		const reports: IReport[] = [];
		const extUrls: IReport[] = [];

		this.data.selected.forEach((selectedReport) => {
			selectedReport.categoryName = this.getCategoryName(formData.assignCategoryControl.id);
			if (selectedReport.groupId?.length > 0) {
				reports.push(selectedReport);
			} else {
				extUrls.push(selectedReport);
			}
		});

		this.reportService.assignCategoryToSelectedReports(formData.assignCategoryControl, reports, extUrls);
		this.dialogRef.close(this.data.selected);
	}

	deleteCategory(category: ReportCategory) {
		const confirmDeleteDialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('insights.administration.reports.categories.confirmdeletecategory', { categoryName: category.name }),
				content: this.translator.instant('insights.administration.reports.categories.confirmdeletecategorytext'),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		confirmDeleteDialogRef.afterClosed().subscribe((res) => {
			if (res === true) {
				this.reportService
					.deleteReportCategory(category.id)
					.pipe(
						tap(() => {
							this.notificationService.notify(['insights.administration.reports.categories.deletedcategory'], ['insights.administration.reports.categories.deletedcategorytext', { categoryName: category.name }], 'success');
							this.reloadCategories();
						}),
					)
					.subscribe();
			}
		});
	}

	editCategory(category: ReportCategory) {
		const simpleEditDialogRef = this.dialog.open(SimpleeditdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('insights.administration.reports.categories.editcategoryheading', { categoryName: category.name }),
				content: this.translator.instant('insights.administration.reports.categories.editcategorytext', { categoryName: category.name }),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
				currentValue: category.name,
			},
		});

		simpleEditDialogRef.afterClosed().subscribe((res) => {
			if (res) {
				this.reportService
					.updateCategory(category.id, res)
					.pipe(
						tap(() => {
							this.reloadCategories();
						}),
					)
					.subscribe();
				// this.availableCategories = this.getSortedCategories();
			}
		});
	}

	closeDialog() {
		this.dialogRef.close();
	}

	drop(event: CdkDragDrop<string[]>) {
		moveItemInArray(this.sortedCategories, event.previousIndex, event.currentIndex);
		this.sortedCategories.map((c, i) => {
			return (c.sortOrder = i + 1);
		});
		this.reportService.saveCategories(this.sortedCategories).subscribe(() => {
			// this.reportService.fetchUserReports();
		});
	}

	cleanCategoryName(name) {
		return name.trim().toLowerCase();
	}

	getCategoryName(categoryId: string | null) {
		if (categoryId) {
			return this.reportService.getReportCategory(categoryId)['name'];
		}
		return '';
	}
}
